/*  global_dpm_model_tab.tsx
    – Model tab of /global-dpm. The model is shown and edited as a matrix:
      one row per security (attributes entered once), one column per risk
      level (its target weight). Storage stays per level
      (riskLevels[risk] = lines[]) — toRows / toLevels convert. Versions list,
      long-format excel upload, validateModel, activate (writeBatch: retire
      the current active, activate this one).
*/
import React, { useMemo, useState } from 'react';
import { ActionIcon, Alert, Badge, Button, FileInput, Group, NumberInput, Select, Stack, Table, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconPlus, IconTrash, IconUpload, IconX } from '@tabler/icons-react';
import * as XLSX from 'xlsx';
import { Timestamp, collection, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { GlobalDpmModel, GlobalDpmModelLine } from '../../types';
import { parseModelSheet, riskLabel, validateModel } from '../../global_dpm_rebalance';
import { Chevron, DetailRow, Panel, useExpanded, useSort } from './global_dpm_ui';
import type { RebalanceData } from './global_dpm_rebalance';

/** One security across every level: attributes once, a target per risk id. */
interface ModelRow extends Omit<GlobalDpmModelLine, 'targetPercent'> {
    targets: Record<string, number>;
}

const toRows = (levels: Record<string, GlobalDpmModelLine[]>): ModelRow[] => {
    const rows = new Map<string, ModelRow>();
    for (const [risk, lines] of Object.entries(levels)) {
        for (const l of lines) {
            const { targetPercent, ...attrs } = l;
            const row = rows.get(l.key) ?? { ...attrs, targets: {} };
            // attributes are per security; the first level that carries a value wins
            row.name ||= attrs.name;
            row.securityNumber ||= attrs.securityNumber;
            row.fundId ||= attrs.fundId;
            row.fallbackKey ||= attrs.fallbackKey;
            row.minHoldingPolicy ||= attrs.minHoldingPolicy;
            row.navBufferPct ||= attrs.navBufferPct;
            row.targets[risk] = targetPercent;
            rows.set(l.key, row);
        }
    }
    return [...rows.values()];
};

/** Every row appears in every level (a 0 target means "not held here — sold if found"). Empty optionals are dropped: Firestore rejects undefined. */
const toLevels = (rows: ModelRow[], riskIds: string[]): Record<string, GlobalDpmModelLine[]> =>
    Object.fromEntries(riskIds.map((risk) => [risk, rows.map((r) => {
        const out: GlobalDpmModelLine = { key: r.key.trim(), kind: r.kind, name: r.name.trim(), targetPercent: Number(r.targets[risk]) || 0 };
        if (r.kind === 'allfunds' && r.securityNumber?.trim()) out.securityNumber = r.securityNumber.trim();
        if (r.kind === 'kfhc') {
            out.minHoldingPolicy = r.minHoldingPolicy ?? 'hold';
            if (r.fundId?.trim()) out.fundId = r.fundId.trim();
            if (r.fallbackKey?.trim()) out.fallbackKey = r.fallbackKey.trim();
            if (r.navBufferPct) out.navBufferPct = r.navBufferPct;
        }
        return out;
    })]));

const emptyRow = (): ModelRow => ({ key: '', kind: 'allfunds', name: '', targets: {} });
const fmtW = (n: number | undefined) => (n === undefined ? '' : `${n % 1 ? n.toFixed(2) : n.toFixed(0)}%`);

/** House-fund rules as one short note. */
const KfhcNote = ({ r, rows, data }: { r: ModelRow; rows: ModelRow[]; data: RebalanceData }) => {
    const fb = r.fallbackKey ? rows.find((x) => x.key === r.fallbackKey) : undefined;
    const found = !!data.funds[r.key];
    return (
        <Text size="xs" c="dimmed">
            {found ? `fund ${r.fundId ?? 'via geneva_investment'}` : <Text span c="red">fund not found</Text>}
            {fb ? ` · below minimum → ${fb.name}` : ''}
            {r.minHoldingPolicy === 'sell_all' ? ' · min holding breach → sell all' : ' · min holding breach → keep minimum'}
            {r.navBufferPct ? ` · NAV +${r.navBufferPct}%` : ''}
        </Text>
    );
};

/** Read-only matrix: securities down, risk levels across. */
export const ModelView = ({ model, data }: { model: Pick<GlobalDpmModel, 'riskLevels'>; data: RebalanceData }) => {
    const risks = Object.keys(model.riskLevels);
    const rows = useMemo(() => toRows(model.riskLevels).sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'allfunds' ? -1 : 1) || Math.max(...risks.map((k) => b.targets[k] ?? 0)) - Math.max(...risks.map((k) => a.targets[k] ?? 0))), [model, risks]);
    const sum = (risk: string, pred: (r: ModelRow) => boolean = () => true) => rows.filter(pred).reduce((s, r) => s + (r.targets[risk] ?? 0), 0);
    const held = (r: ModelRow) => risks.some((k) => (r.targets[k] ?? 0) > 0);
    const cell = (r: ModelRow, risk: string) => {
        const t = r.targets[risk] ?? 0;
        return <Table.Td key={risk} ta="right" fw={t > 0 ? 600 : undefined} c={t > 0 ? undefined : 'dimmed'} bg={t > 0 ? `rgba(34, 139, 230, ${0.05 + Math.min(t, 40) / 100})` : undefined}>{t > 0 ? fmtW(t) : '·'}</Table.Td>;
    };
    const section = (label: string, pred: (r: ModelRow) => boolean) => (
        <Table.Tr key={label} fw={600} bg="var(--mantine-color-gray-0)"><Table.Td>{label}</Table.Td><Table.Td /><Table.Td />{risks.map((k) => <Table.Td key={k} ta="right">{fmtW(sum(k, pred))}</Table.Td>)}</Table.Tr>
    );
    return (
        <Table fz="sm" highlightOnHover withColumnBorders>
            <Table.Thead>
                <Table.Tr><Table.Th>Security</Table.Th><Table.Th>Kind</Table.Th><Table.Th>Sec No</Table.Th>{risks.map((k) => <Table.Th key={k} ta="right">{riskLabel(data.riskLevels, k)}</Table.Th>)}</Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {rows.filter(held).map((r) => (
                    <Table.Tr key={r.key}>
                        <Table.Td>
                            <Text size="sm" fw={500}>{r.name}</Text>
                            <Text size="xs" c="dimmed">{r.key}</Text>
                            {r.kind === 'kfhc' && <KfhcNote r={r} rows={rows} data={data} />}
                        </Table.Td>
                        <Table.Td>{r.kind === 'kfhc' ? <Badge size="xs" color="grape" variant="light">house fund</Badge> : <Text size="xs">AllFunds</Text>}</Table.Td>
                        <Table.Td><Text size="xs">{r.securityNumber ?? ''}</Text></Table.Td>
                        {risks.map((k) => cell(r, k))}
                    </Table.Tr>
                ))}
                {section('AllFunds', (r) => r.kind === 'allfunds')}
                {section('House funds', (r) => r.kind === 'kfhc')}
                <Table.Tr fw={700}><Table.Td>Total</Table.Td><Table.Td /><Table.Td />{risks.map((k) => { const s = sum(k); return <Table.Td key={k} ta="right" c={Math.abs(s - 100) < 0.01 ? 'green' : 'red'}>{fmtW(s)}</Table.Td>; })}</Table.Tr>
                {rows.some((r) => !held(r)) && <Table.Tr><Table.Td colSpan={3 + risks.length}><Text size="xs" c="dimmed">In the universe but not held in any level (sold if found): {rows.filter((r) => !held(r)).map((r) => r.name).join(', ')}</Text></Table.Td></Table.Tr>}
            </Table.Tbody>
        </Table>
    );
};

/* ========================= tab ========================= */

const GlobalDpmModelTab = ({ data }: { data: RebalanceData }) => {
    const { db, riskIds, riskLevels, models, userEmail } = data;
    const [label, setLabel] = useState('');
    const [rows, setRows] = useState<ModelRow[] | null>(null);
    const [busy, setBusy] = useState(false);
    const { isOpen, toggle } = useExpanded();
    const { sorted, th } = useSort(models, { label: (m) => m.label, status: (m) => m.status, created: (m) => m.createdAt?.toMillis(), effective: (m) => m.effectiveFrom?.toMillis() }, 'created', 'desc');

    const levels = useMemo(() => (rows ? toLevels(rows, riskIds) : null), [rows, riskIds]);
    const validation = useMemo(() => (levels ? validateModel(levels, riskIds) : { errors: [], warnings: [] }), [levels, riskIds]);

    const load = (m: GlobalDpmModel) => { setLabel(`${m.label} (copy)`); setRows(toRows(m.riskLevels)); };
    const startBlank = () => { setLabel(''); setRows([emptyRow()]); };
    const upload = async (f: File | null) => {
        if (!f) return;
        try {
            const workbook = XLSX.read(await f.arrayBuffer(), { type: 'array' });
            const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[workbook.SheetNames[0]], { defval: '' });
            const parsed = parseModelSheet(json);
            if (Object.keys(parsed).length === 0) throw new Error('no rows found — expected columns Risk Level, Key, Kind, Name, Security Number, Target %, Fallback Key, Min Holding Policy, Fund ID, NAV Buffer %');
            setRows(toRows(parsed));
            if (!label) setLabel(f.name.replace(/\.xlsx?$/i, ''));
        } catch (e) {
            notifications.show({ title: 'Model upload failed', message: String((e as Error).message ?? e), color: 'red', icon: <IconX /> });
        }
    };
    const setRow = (i: number, patch: Partial<ModelRow>) => setRows((prev) => prev!.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    const setTarget = (i: number, risk: string, v: number) => setRows((prev) => prev!.map((r, j) => (j === i ? { ...r, targets: { ...r.targets, [risk]: v } } : r)));

    const save = async () => {
        if (!levels || validation.errors.length || !label.trim()) return;
        setBusy(true);
        try {
            const ref = doc(collection(db, 'global_dpm_models'));
            const batch = writeBatch(db);
            batch.set(ref, { label: label.trim(), status: 'draft', createdBy: userEmail, createdAt: Timestamp.now(), riskLevels: levels });
            await batch.commit();
            notifications.show({ title: 'Model saved', message: `${label} saved as draft — activate it when ready`, color: 'green', icon: <IconCheck /> });
            setRows(null);
            setLabel('');
        } catch (e) {
            console.error(e);
            notifications.show({ title: 'Save failed', message: 'Check console', color: 'red', icon: <IconX /> });
        }
        setBusy(false);
    };

    const activate = async (m: GlobalDpmModel) => {
        const { errors } = validateModel(m.riskLevels, riskIds);
        if (errors.length) { notifications.show({ title: 'Cannot activate', message: errors.join('; '), color: 'red' }); return; }
        if (!window.confirm(`Activate "${m.label}"? Every portfolio will be compared to it from now on${data.activeModel ? `; "${data.activeModel.label}" is retired` : ''}.`)) return;
        setBusy(true);
        try {
            const batch = writeBatch(db);
            models.filter((x) => x.status === 'active' && x.id !== m.id).forEach((x) => batch.update(doc(db, 'global_dpm_models', x.id), { status: 'retired' }));
            batch.update(doc(db, 'global_dpm_models', m.id), { status: 'active', effectiveFrom: Timestamp.now() });
            await batch.commit();
            notifications.show({ title: 'Model activated', message: m.label, color: 'green', icon: <IconCheck /> });
        } catch (e) {
            console.error(e);
            notifications.show({ title: 'Activation failed', message: 'Check console', color: 'red', icon: <IconX /> });
        }
        setBusy(false);
    };

    /** Name only — securities / targets change through "Edit as new", so a batch always points at the content it was computed from. Batches keep the label they were saved with. */
    const rename = async (m: GlobalDpmModel) => {
        const next = window.prompt('Model name', m.label)?.trim();
        if (!next || next === m.label) return;
        try {
            await updateDoc(doc(db, 'global_dpm_models', m.id), { label: next });
            notifications.show({ title: 'Model renamed', message: `${m.label} → ${next}`, color: 'green', icon: <IconCheck /> });
        } catch (e) {
            console.error(e);
            notifications.show({ title: 'Rename failed', message: 'Check console', color: 'red', icon: <IconX /> });
        }
    };

    const colSum = (risk: string) => (rows ?? []).reduce((s, r) => s + (Number(r.targets[risk]) || 0), 0);

    return (
        <Stack>
            {data.activeModel && (
                <Panel>
                    <Stack gap="xs">
                        <Group><Title order={4}>Current model</Title><Badge color="green">{data.activeModel.label}</Badge><Text size="xs" c="dimmed">effective {data.activeModel.effectiveFrom?.toDate().toLocaleDateString('en-GB') ?? '—'}</Text></Group>
                        <ModelView model={data.activeModel} data={data} />
                    </Stack>
                </Panel>
            )}
            <Group justify="space-between">
                <Title order={4}>Versions</Title>
                <Group>
                    <FileInput placeholder="Upload model excel" accept=".xlsx,.xls" leftSection={<IconUpload size={16} />} onChange={upload} clearable w={240} />
                    <Button variant="light" onClick={startBlank}>New blank model</Button>
                </Group>
            </Group>
            <Panel>
                <Table fz="sm" highlightOnHover>
                    <Table.Thead><Table.Tr><Table.Th w={24} />{th('label', 'Name')}{th('status', 'Status')}{th('created', 'Created')}{th('effective', 'Effective')}<Table.Th>Securities</Table.Th><Table.Th /></Table.Tr></Table.Thead>
                    <Table.Tbody>
                        {models.length === 0 && <Table.Tr><Table.Td colSpan={7}><Text c="dimmed">No model yet — upload the excel or start a blank one.</Text></Table.Td></Table.Tr>}
                        {sorted.map((m) => (
                            <React.Fragment key={m.id}>
                                <Table.Tr onClick={() => toggle(m.id)} style={{ cursor: 'pointer' }}>
                                    <Table.Td><Chevron open={isOpen(m.id)} /></Table.Td>
                                    <Table.Td>{m.label}</Table.Td>
                                    <Table.Td><Badge color={m.status === 'active' ? 'green' : m.status === 'draft' ? 'yellow' : 'gray'}>{m.status}</Badge></Table.Td>
                                    <Table.Td>{m.createdAt?.toDate().toLocaleString('en-GB')} · {m.createdBy}</Table.Td>
                                    <Table.Td>{m.effectiveFrom?.toDate().toLocaleDateString('en-GB') ?? '—'}</Table.Td>
                                    <Table.Td>{toRows(m.riskLevels).length} · {Object.keys(m.riskLevels).map((r) => riskLabel(riskLevels, r)).join(' / ')}</Table.Td>
                                    <Table.Td onClick={(e) => e.stopPropagation()}>
                                        <Group gap="xs">
                                            <Button size="compact-xs" variant="subtle" onClick={() => rename(m)}>Rename</Button>
                                            <Button size="compact-xs" variant="subtle" onClick={() => load(m)}>Edit as new</Button>
                                            {m.status !== 'active' && <Button size="compact-xs" onClick={() => activate(m)} loading={busy}>Activate</Button>}
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                                <DetailRow open={isOpen(m.id)} colSpan={7}><ModelView model={m} data={data} /></DetailRow>
                            </React.Fragment>
                        ))}
                    </Table.Tbody>
                </Table>
            </Panel>

            {rows && (
                <Panel>
                    <Stack>
                        <Group>
                            <Title order={4}>Editor</Title>
                            <TextInput placeholder="Name — e.g. v2 — Sept 2026 IC" value={label} onChange={(e) => setLabel(e.currentTarget.value)} w={320} />
                            <Button onClick={save} disabled={validation.errors.length > 0 || !label.trim()} loading={busy}>Save as draft</Button>
                            <Button variant="subtle" color="gray" onClick={() => { setRows(null); setLabel(''); }}>Discard</Button>
                            <Button variant="light" leftSection={<IconPlus size={14} />} onClick={() => setRows((p) => [...(p ?? []), emptyRow()])}>Add security</Button>
                        </Group>
                        {validation.errors.length > 0 && <Alert color="red" title="Not valid">{validation.errors.map((e) => <Text key={e} size="sm">{e}</Text>)}</Alert>}
                        {validation.warnings.length > 0 && <Alert color="yellow" title="Warnings">{validation.warnings.map((e) => <Text key={e} size="sm">{e}</Text>)}</Alert>}
                        <Table fz="xs" withColumnBorders>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Key (ISIN / Geneva code)</Table.Th><Table.Th>Kind</Table.Th><Table.Th>Name</Table.Th><Table.Th>Security Number</Table.Th><Table.Th>Fund · fallback · min holding · NAV buffer (house funds)</Table.Th>
                                    {riskIds.map((risk) => <Table.Th key={risk} ta="right"><Stack gap={0} align="end"><span>{riskLabel(riskLevels, risk)}</span><Badge size="xs" color={Math.abs(colSum(risk) - 100) < 0.01 ? 'green' : 'red'}>Σ {colSum(risk).toFixed(2)}%</Badge></Stack></Table.Th>)}
                                    <Table.Th />
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>{rows.map((r, i) => (
                                <Table.Tr key={i}>
                                    <Table.Td><TextInput size="xs" value={r.key} onChange={(e) => setRow(i, { key: e.currentTarget.value })} w={150} /></Table.Td>
                                    <Table.Td><Select size="xs" data={[{ value: 'allfunds', label: 'AllFunds' }, { value: 'kfhc', label: 'House fund' }]} value={r.kind} onChange={(v) => setRow(i, { kind: (v as 'allfunds' | 'kfhc') ?? 'allfunds' })} w={110} /></Table.Td>
                                    <Table.Td><TextInput size="xs" value={r.name} onChange={(e) => setRow(i, { name: e.currentTarget.value })} w={260} /></Table.Td>
                                    <Table.Td><TextInput size="xs" value={r.securityNumber ?? ''} onChange={(e) => setRow(i, { securityNumber: e.currentTarget.value })} disabled={r.kind === 'kfhc'} w={100} /></Table.Td>
                                    <Table.Td>
                                        {r.kind === 'kfhc' ? (
                                            <Group gap={4} wrap="nowrap">
                                                <Select size="xs" searchable clearable placeholder="fund document" data={data.fundOptions} value={r.fundId ?? null} onChange={(v) => setRow(i, { fundId: v ?? undefined })} w={190} />
                                                <Select size="xs" clearable placeholder="fallback" data={rows.filter((x) => x.key && x.key !== r.key).map((x) => ({ value: x.key, label: `${x.name || x.key}${x.kind === 'kfhc' ? ' (house fund)' : ''}` }))} value={r.fallbackKey ?? null} onChange={(v) => setRow(i, { fallbackKey: v ?? undefined })} w={170} />
                                                <Tooltip label="What to do when a redemption would leave less than the fund's minimum holding"><Select size="xs" data={[{ value: 'hold', label: 'keep minimum' }, { value: 'sell_all', label: 'sell all' }]} value={r.minHoldingPolicy ?? 'hold'} onChange={(v) => setRow(i, { minHoldingPolicy: (v as 'hold' | 'sell_all') ?? 'hold' })} w={120} /></Tooltip>
                                                <Tooltip label="Added to the last NAV when sizing a subscription — the next NAV may be higher (e.g. 0.5% USD, 1% KWD for the FX too)"><NumberInput size="xs" placeholder="NAV buffer" value={r.navBufferPct ?? ''} onChange={(v) => setRow(i, { navBufferPct: Number(v) || undefined })} min={0} step={0.1} decimalScale={2} suffix="%" w={90} hideControls /></Tooltip>
                                            </Group>
                                        ) : <Text size="xs" c="dimmed">—</Text>}
                                    </Table.Td>
                                    {riskIds.map((risk) => (
                                        <Table.Td key={risk} ta="right" bg={(r.targets[risk] ?? 0) > 0 ? 'rgba(34, 139, 230, 0.06)' : undefined}>
                                            <NumberInput size="xs" value={r.targets[risk] ?? 0} onChange={(v) => setTarget(i, risk, Number(v) || 0)} min={0} max={100} decimalScale={2} suffix="%" w={90} hideControls styles={{ input: { textAlign: 'right' } }} />
                                        </Table.Td>
                                    ))}
                                    <Table.Td><ActionIcon variant="subtle" color="red" onClick={() => setRows((p) => p!.filter((_, j) => j !== i))}><IconTrash size={14} /></ActionIcon></Table.Td>
                                </Table.Tr>
                            ))}</Table.Tbody>
                        </Table>
                    </Stack>
                </Panel>
            )}
        </Stack>
    );
};

export default GlobalDpmModelTab;
